/**
 * MemberJourney — cross-source activity timeline for every user.
 *
 * Reads /api/me/journey which returns fitness_journey_events across
 * ALL event_types (workouts, food logs, morning check-ins, plans,
 * bookings, and Arena events too). Kept intentionally separate from
 * ArenaJourney.jsx which stays frozen.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Activity, Utensils, Sun, ClipboardList, CalendarCheck, Dumbbell, Flame, Award } from "lucide-react";
import { apiClient } from "../../services/api";
import TopBar from "../../components/layout/TopBar";
import BottomNav from "../../components/layout/BottomNav";

const KIND = {
  workout_completed:  { Icon: Dumbbell,      color: "#00B0D5", label: "Workout" },
  food_logged:        { Icon: Utensils,      color: "#F5C518", label: "Food log" },
  morning_checkin:    { Icon: Sun,           color: "#F59E0B", label: "Morning check-in" },
  plan_received:      { Icon: ClipboardList, color: "#7C3AED", label: "Plan received" },
  booking_confirmed:  { Icon: CalendarCheck, color: "#0F766E", label: "Booking" },
  arena_pass_started: { Icon: Flame,         color: "#EF4444", label: "Arena pass" },
  arena_checkin:      { Icon: Flame,         color: "#EF4444", label: "Arena check-in" },
  gym_linked:         { Icon: Award,         color: "#0F766E", label: "Gym linked" },
  trainer_note_received: { Icon: ClipboardList, color: "#7C3AED", label: "Trainer note" },
};

function summarise(e) {
  const m = e.event_metadata || {};
  switch (e.event_type) {
    case "workout_completed":  return `${m.exercises_count || 0} exercises · ${m.duration_minutes || "?"} min${m.module ? ` · ${m.module}` : ""}`;
    case "food_logged":        return "Meals logged for the day";
    case "morning_checkin":    return `Score ${m.score ?? "?"}`;
    case "plan_received":      return `${m.plan_name || m.plan_type || "Plan"} from ${m.from_pro || "your pro"}`;
    case "booking_confirmed":  return `${m.pro_name || "pro"} · ${m.amount_inr ? `₹${m.amount_inr}` : "Free intro"}`;
    default:                   return null;
  }
}

function fmtWhen(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

export default function MemberJourney() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const load = useCallback(async (before) => {
    if (before) setLoadingMore(true); else setLoading(true);
    try {
      const params = { limit: 60 };
      if (before) params.before = before;
      const { data: r } = await apiClient.get("/me/journey", { params });
      setData((prev) => {
        if (!before) return r;
        return { ...r, events: [...(prev?.events || []), ...r.events], stats: prev?.stats || r.stats };
      });
    } catch { /* keep the page usable */ }
    finally { setLoading(false); setLoadingMore(false); }
  }, []);

  useEffect(() => { load(null); }, [load]);

  const openDay = (iso) => {
    const date = (iso || "").slice(0, 10);
    if (date) navigate(`/me/day/${date}`);
  };

  return (
    <div className="min-h-screen" style={{ background: "#F4F3F1", paddingBottom: 96 }} data-testid="member-journey">
      <TopBar />
      <main style={{ maxWidth: 640, margin: "0 auto", padding: "16px 16px 40px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
          <button onClick={() => navigate(-1)} aria-label="Back" data-testid="journey-back"
            style={{ width: 36, height: 36, borderRadius: "50%", background: "white", border: "1px solid #E7E5E1", display: "grid", placeItems: "center", cursor: "pointer" }}>
            <ChevronLeft size={18} color="#0C0C0B" />
          </button>
          <h1 style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800, fontSize: 26, color: "#0C0C0B", margin: 0 }}>My journey</h1>
        </div>

        {/* Stats — all activity, not just Arena */}
        {data?.stats && (
          <div data-testid="journey-stats" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginBottom: 16 }}>
            {[
              { k: "workouts", label: "Workouts" },
              { k: "checkins", label: "Check-ins" },
              { k: "total_reps", label: "Total reps" },
              { k: "longest_streak", label: "Longest streak" },
            ].map((s) => (
              <div key={s.k} style={{ background: "white", border: "1px solid #E7E5E1", borderRadius: 12, padding: "10px 8px", textAlign: "center" }}>
                <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800, fontSize: 22, color: "#0C0C0B", lineHeight: 1 }}>{data.stats[s.k] ?? 0}</div>
                <div style={{ fontSize: 10, color: "#8A8884", marginTop: 4, textTransform: "uppercase", letterSpacing: "0.05em" }}>{s.label}</div>
              </div>
            ))}
          </div>
        )}

        {loading && <div style={{ padding: 40, textAlign: "center", color: "#8A8884" }}>Loading…</div>}

        {!loading && data?.weeks?.length === 0 && (
          <div data-testid="journey-empty" style={{ padding: "40px 16px", textAlign: "center", color: "#8A8884" }}>
            Nothing logged yet — complete a workout or log a meal to fill your journey.
          </div>
        )}

        {!loading && data?.weeks?.map((wk) => (
          <div key={wk.week_start} style={{ marginBottom: 20 }} data-testid={`journey-week-${wk.week_start}`}>
            <div style={{ fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", color: "#8A8884", fontWeight: 700, marginBottom: 8 }}>
              Week of {new Date(wk.week_start).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {wk.events.map((e) => {
                const meta = KIND[e.event_type] || { Icon: Activity, color: "#6B7280", label: e.event_type };
                const Icon = meta.Icon;
                const sum = summarise(e);
                return (
                  <button
                    key={e.event_id || `${e.event_type}-${e.event_at}`}
                    onClick={() => openDay(e.event_at)}
                    data-testid={`journey-row-${e.event_type}`}
                    style={{ textAlign: "left", display: "flex", gap: 10, alignItems: "flex-start",
                      padding: "12px 14px", background: "white",
                      border: "1px solid #EEECE9", borderRadius: 12, cursor: "pointer" }}
                  >
                    <div style={{ width: 36, height: 36, borderRadius: 10, background: `${meta.color}22`, display: "grid", placeItems: "center", flexShrink: 0 }}>
                      <Icon size={18} color={meta.color} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 11, color: "#8A8884" }}>{fmtWhen(e.event_at)} · {meta.label}</div>
                      {sum && <div style={{ fontSize: 13, color: "#0C0C0B", marginTop: 2 }}>{sum}</div>}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ))}

        {data?.next_before && (
          <button
            onClick={() => load(data.next_before)}
            disabled={loadingMore}
            data-testid="journey-load-more"
            style={{ width: "100%", padding: 12, borderRadius: 10, background: "white", border: "1px solid #E7E5E1", fontSize: 13, fontWeight: 700, cursor: "pointer" }}
          >
            {loadingMore ? "Loading…" : "Load more"}
          </button>
        )}
      </main>
      <BottomNav />
    </div>
  );
}
